/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Florian Hartl <f.hartl@techdivision.com>
 */

import React from 'react';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useMediaQuery } from '@mui/material';
import theme from '../../Config/ThemeConfig';
import { FormStepper } from '../../Components/FormStepper';
import { useCheckout } from './useCheckout';
import { SuccessForm } from './Components/SuccessForm';
import { checkoutFormModel } from './FormModel/FormModel';
import FormStepperContent from '../../Components/FormStepper/Components/FormStepperContent';
import FormStepperActions from '../../Components/FormStepper/Components/FormStepperActions';
import { FullScreenLoadingOverlay } from '../../Components/LoadingOverlay/FullScreenLoadingOverlay';
import { isFormLoading } from '../../Signals/formStateSignal';

/**
 * Checkout Component
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export default function Checkout(props) {
  const { stepData } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const {
    activeStep,
    lastValidStep,
    initialFormValues,
    validationSchema,
    setValidationSchema,
    checkoutIsInitialized,
    handleBack,
    handleSubmit,
    handleStepperIconClick,
  } = useCheckout({ stepperLength: stepData.length });

  const { formId } = checkoutFormModel;
  const currentStepData = stepData[activeStep];

  return (
    <>
      {isFormLoading.value && <FullScreenLoadingOverlay />}
      {checkoutIsInitialized ? (
        <FormStepper
          stepperData={stepData}
          setActiveStep={handleStepperIconClick}
          activeStep={activeStep}
          lastValidStep={lastValidStep}
          isMobile={isMobile}
        >
          {activeStep === stepData.length ? (
            <SuccessForm />
          ) : (
            <Formik
              initialValues={initialFormValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnChange={false}
              validateOnBlur
            >
              <Form id={formId}>
                <FormStepperContent
                  step={currentStepData}
                  validationSchema={validationSchema}
                  setValidationSchema={setValidationSchema}
                />
                <FormStepperActions
                  handleBack={handleBack}
                  activeStep={activeStep}
                  nextLabel={currentStepData.nextLabel}
                  previousLabel={currentStepData.previousLabel}
                  isMobile={isMobile}
                />
              </Form>
            </Formik>
          )}
        </FormStepper>
      ) : (
        <FullScreenLoadingOverlay />
      )}
    </>
  );
}

Checkout.propTypes = {
  stepData: PropTypes.array.isRequired,
};
